import { api, API_BASE } from './client';

const tok = () => sessionStorage.getItem('cl_token') ?? '';

export interface AdminClient {
  id: string;
  name: string;
  business: string;
  phone?: string;
  email?: string;
  city?: string;
  state?: string;
  appVersion: string;
  notes?: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
  _count?: { subscriptions: number; licenseKeys: number };
}

export interface LicenseKey {
  id: string;
  keyValue: string;
  clientId?: string;
  client?: { name: string; business: string };
  tier: string;
  status: 'active' | 'revoked' | 'expired';
  expiresAt?: string;
  notes?: string;
  createdAt: string;
}

export interface AdminSubscription {
  id: string;
  clientId: string;
  client?: { name: string; business: string };
  plan: string;
  amount: number;
  paymentMode: string;
  paymentDate: string;
  expiresAt: string;
  notes?: string;
  createdAt: string;
}

export interface AdminImport {
  id: string;
  clientId: string;
  client?: { name: string; business: string };
  importDate: string;
  businessName?: string;
  exportDate?: string;
  invoiceCount: number;
  partyCount: number;
  productCount: number;
  expenseCount: number;
  createdAt: string;
}

export interface RenewalItem extends AdminSubscription {
  daysUntilExpiry: number;
  isExpired: boolean;
  client: { id: string; name: string; business: string; phone?: string; email?: string };
}

export interface RevenueTrendPoint {
  month: string;
  revenue: number;
}

export interface AdminDashboard {
  totalClients: number;
  activePremium: number;
  expiringSoon: number;
  revenueThisMonth: number;
  totalRevenue: number;
  masterKey: string;
  recentClients: AdminClient[];
  recentSubscriptions: AdminSubscription[];
}

export const adminApi = {
  // Dashboard
  getDashboard: () => api.get<AdminDashboard>('/admin/dashboard'),
  getMasterKey: () => api.get<{ key: string }>('/admin/master-key'),
  getRenewalQueue: () => api.get<RenewalItem[]>('/admin/renewal-queue'),
  getRevenueTrend: (months = 12) => api.get<RevenueTrendPoint[]>(`/admin/revenue-trend?months=${months}`),

  // Clients
  listClients: (search?: string) => api.get<AdminClient[]>(`/admin/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getClient: (id: string) => api.get<AdminClient & { licenseKeys: LicenseKey[]; subscriptions: AdminSubscription[]; imports: AdminImport[] }>(`/admin/clients/${id}`),
  createClient: (data: Partial<AdminClient>) => api.post<AdminClient>('/admin/clients', data),
  updateClient: (id: string, data: Partial<AdminClient>) => api.patch<AdminClient>(`/admin/clients/${id}`, data),
  deleteClient: (id: string) => api.delete<{ success: boolean }>(`/admin/clients/${id}`),

  // License Keys
  listKeys: () => api.get<LicenseKey[]>('/admin/license-keys'),
  createKey: (data: { clientId?: string; expiresAt?: string; notes?: string }) => api.post<LicenseKey>('/admin/license-keys', data),
  updateKey: (id: string, data: { clientId?: string | null; status?: string; expiresAt?: string | null; notes?: string }) =>
    api.patch<LicenseKey>(`/admin/license-keys/${id}`, data),
  deleteKey: (id: string) => api.delete<{ success: boolean }>(`/admin/license-keys/${id}`),

  // Subscriptions
  listSubscriptions: (clientId?: string) => api.get<AdminSubscription[]>(`/admin/subscriptions${clientId ? `?clientId=${clientId}` : ''}`),
  createSubscription: (data: { clientId: string; plan?: string; amount: number; paymentMode?: string; paymentDate: string; expiresAt: string; notes?: string }) =>
    api.post<AdminSubscription>('/admin/subscriptions', data),
  deleteSubscription: (id: string) => api.delete<{ success: boolean }>(`/admin/subscriptions/${id}`),

  // Imports
  listImports: (clientId?: string) => api.get<AdminImport[]>(`/admin/imports${clientId ? `?clientId=${clientId}` : ''}`),
  getImportData: (id: string) => api.get<{ data: Record<string, unknown[]> }>(`/admin/imports/${id}/data`),
  uploadImport: async (clientId: string, file: File): Promise<AdminImport> => {
    const fd = new FormData(); fd.append('file', file);
    const res = await fetch(`${API_BASE}/admin/clients/${clientId}/imports`, {
      method: 'POST', headers: { Authorization: `Bearer ${tok()}` }, body: fd,
    });
    if (!res.ok) { const b = await res.json().catch(() => ({})) as { message?: string }; throw new Error(b.message ?? `Upload failed (${res.status})`); }
    return res.json() as Promise<AdminImport>;
  },
  deleteImport: (id: string) => api.delete<{ success: boolean }>(`/admin/imports/${id}`),
};
